import { useCallback, useRef, useState } from 'react';

import {
  CheckSquare,
  ChevronDown,
  Info,
  Loader2,
  Maximize2,
  Minimize2,
  Printer,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { useFreezePdfPrint } from '@/components/hooks/useFreezePdfPrint';
import type {
  LiveEmbryoForPrint,
  TempEmbryoForPrint,
} from '@/components/hooks/useFreezePdfPrint/types';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TOA_GetFertilizationsForFreezing } from '@/rtk-query.gql';
import type {
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
} from '@/services/freeze';
import {
  useFreezeLabelsReport,
  useFreezeTemplateReport,
  useFreezeValidationReport,
} from '@/services/reports';
import { useSiteByIdQuery } from '@/services/sites';
import { formatDateString } from '@/utils';

import {
  useClearSelection,
  useFreezeForm,
  useFreezeGrid,
  useFreezeSelection,
} from '../contexts';
import type { DayFilter } from '../hooks/useDayFilter';
import {
  useDeactivateInterimEmbryosMutation,
  useUpdateODFreezeMutation,
} from '../services/freezeApi';
import type { UpdateODFreezeRequest } from '../services/freezeApi';
import { MOBILE_BREAKPOINT } from '../utils/constants';
import {
  ConfirmDialog,
  EmbryoSummaryDialog,
  type IncompleteEmbryo,
  IncompleteEmbryosDialog,
  RulesDialog,
  SelectionSummaryDialog,
} from './dialogs';

type FreezeActionBarProps = {
  onRefresh: () => void;
  isLoading: boolean;
  fertilizations?: FertilizationListFreezeResponse[];
  embryosByFert?: Map<string, FertAndEmbryosResponse>;
  activeDayFilter?: DayFilter;
  onDayFilterChange?: (filter: DayFilter) => void;
};

export const FreezeActionBar = ({
  onRefresh,
  isLoading,
  fertilizations = [],
  embryosByFert,
  activeDayFilter = 'all',
  onDayFilterChange,
}: FreezeActionBarProps) => {
  const dispatch = useDispatch();
  const { isExpanded, setIsExpanded, setIsUpdateRunning, gridApi } =
    useFreezeGrid();
  const selection = useFreezeSelection();
  const clearSelection = useClearSelection();

  /* WI 37941 #9: matings stayed open after Update OvaDrive. Collapse every master row and reset
     the toolbar's expand/collapse state, otherwise isExpanded stays true and the next refetch
     re-expands the rebuilt rows. */
  const collapseAllMatings = useCallback(() => {
    if (!gridApi) return;
    gridApi.forEachNode((node) => {
      if (node.master) node.setExpanded(false);
    });
    setIsExpanded(false);
  }, [gridApi, setIsExpanded]);

  const { batchForm, qcForm } = useFreezeForm();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const [params] = useSearchParams();
  /**
   * The summary is scoped to the fertilization site the user picked, so it has
   * to be resolved from `sId` the way Freeze v1 does. v2 previously showed
   * `fertilizations[0].opuSite` — the *aspiration* site of whichever fert
   * happened to sort first, e.g. DG where the user selected MAC (WI 37941 #14).
   */
  const { data: site } = useSiteByIdQuery(params.get('sId') ?? undefined);
  const siteName = site?.name ?? '';

  const { mutateAsync: updateODFreeze, isPending: isUpdating } =
    useUpdateODFreezeMutation();
  const { mutateAsync: deactivateEmbryos, isPending: isDiscarding } =
    useDeactivateInterimEmbryosMutation();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showIncompleteDialog, setShowIncompleteDialog] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [showEmbryoSummaryDialog, setShowEmbryoSummaryDialog] = useState(false);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const incompleteEmbryosRef = useRef<IncompleteEmbryo[]>([]);

  const [generateValidationReport, { isPending: isGenValidation }] =
    useFreezeValidationReport();
  const [generateTemplateReport, { isPending: isGenTemplate }] =
    useFreezeTemplateReport();
  const [generateFreezeLabels, { isPending: isGenFreezeLabels }] =
    useFreezeLabelsReport();

  const getEmbryoData = useCallback(() => {
    const { fertilizationIds, tempEmbryoIds, liveEmbryoIds } = selection;

    const selectedTempEmbryos: TempEmbryoForPrint[] = [];
    const selectedLiveEmbryos: LiveEmbryoForPrint[] = [];

    embryosByFert?.forEach((data) => {
      for (const e of data.tempEmbryos ?? []) {
        if (
          e.embryoFreezeEntryId &&
          tempEmbryoIds.includes(e.embryoFreezeEntryId)
        ) {
          selectedTempEmbryos.push(e);
        }
      }
      for (const e of data.liveEmbryos ?? []) {
        if (e.id && liveEmbryoIds.includes(e.id)) {
          selectedLiveEmbryos.push(e);
        }
      }
    });

    const selectedFertilizations = fertilizations.filter(
      (f) => f.fertId && fertilizationIds.includes(f.fertId),
    );

    return {
      selectedTempEmbryos,
      selectedLiveEmbryos,
      // processEmbryosForPrint only reads fertId + plateNumber, both present here.
      fertilizations:
        selectedFertilizations as unknown as TOA_GetFertilizationsForFreezing[],
    };
  }, [selection, embryosByFert, fertilizations]);

  const { printPdf, isPrinting } = useFreezePdfPrint({ getEmbryoData });

  const handleLabelPrint = useCallback(
    (filename: 'GobletLabel' | 'StrawLabel' | 'CaneTabLabel') => {
      if (
        selection.tempEmbryoIds.length === 0 &&
        selection.liveEmbryoIds.length === 0
      ) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'Select embryos first',
            life: 3000,
          }),
        );
        return;
      }
      printPdf(filename);
    },
    [selection, printPdf, dispatch],
  );

  const handleRunSheet = useCallback(() => {
    window.open(
      '/templates/Freeze Run Template Horizontal-Rev_3.PDF',
      '_blank',
    );
  }, []);

  const handleVitrificationRunSheet = useCallback(() => {
    window.open('/templates/Vit Run Template-Rev_2.PDF', '_blank');
  }, []);

  const isAnyReportGenerating =
    isGenValidation || isGenTemplate || isGenFreezeLabels || isPrinting;

  const hasSelection =
    selection.fertilizationIds.length > 0 ||
    selection.tempEmbryoIds.length > 0 ||
    selection.liveEmbryoIds.length > 0;

  /** Execute the actual OvaDrive update (after any pre-checks pass) */
  const executeUpdate = useCallback(async () => {
    setIsUpdateRunning(true);
    try {
      let hasErrors = false;

      for (const fertId of selection.fertilizationIds) {
        const fert = fertilizations.find((f) => f.fertId === fertId);
        if (!fert) continue;

        const embryoData = embryosByFert?.get(fertId);
        // Only push the embryos the user actually selected (matches v1).
        const intEmbIDs =
          embryoData?.tempEmbryos
            .map((e) => e.embryoFreezeEntryId)
            .filter(
              (id): id is string =>
                !!id && selection.tempEmbryoIds.includes(id),
            ) ?? [];
        const embIDs =
          embryoData?.liveEmbryos
            .map((e) => e.id)
            .filter(
              (id): id is string =>
                !!id && selection.liveEmbryoIds.includes(id),
            )
            .join(',') ?? '';

        // QC expiration dates are stored as ISO strings; the backend expects MM-dd-yyyy (matches v1).
        const toMdy = (d: string | null | undefined) =>
          d ? formatDateString(d, 'MM-dd-yyyy') : null;

        // These map to Guid? on the API — empty strings fail model binding (400).
        // Coerce any empty value to null, mirroring v1's payload.
        const input: UpdateODFreezeRequest = {
          fertID: fertId,
          intEmbIDs,
          embIDs,
          storageLocID: batchForm.storageLocationId || null,
          freezeLocationId: batchForm.freezeLocation || null,
          freezeDay:
            batchForm.freezeDay != null ? String(batchForm.freezeDay) : null,
          predictionTechID: batchForm.predictionTechId || null,
          graderID: batchForm.graderId || null,
          freezeTechID: batchForm.freezeTechId || null,
          freezer: batchForm.freezer || null,
          embsPerVitStraw: batchForm.embryosPerVitStraw?.toString() ?? null,
          embsPerStraw: batchForm.embryosPerStraw?.toString() ?? null,
          eg: qcForm.egLotNumber || null,
          egDate: toMdy(qcForm.egExpDate),
          trypsinDate: toMdy(qcForm.trypsinExpDate),
          sucroseDate: toMdy(qcForm.sucroseExpDate),
          ehmDate: toMdy(qcForm.ehmExpDate),
          freezingComment: qcForm.freezingComment || null,
          esDate: toMdy(qcForm.esExpDate),
          vsDate: toMdy(qcForm.vsExpDate),
          strawLotNo: qcForm.strawLotNumber || null,
          qcSterileStrawLotNumber: qcForm.sterileStrawLotNumber || null,
          vitStrawLotNo: qcForm.vitStrawLotNumber || null,
          qc12WellPlates: qcForm.twelveWellLot || null,
        };

        try {
          await updateODFreeze({ input, dtplus: batchForm.dtPlus ?? false });
        } catch (error) {
          hasErrors = true;
          dispatch(
            addToast({
              severity: 'error',
              summary: 'Update Failed',
              detail: `Failed to update fert ${fert.plateNumber}: ${error}`,
              life: 5000,
            }),
          );
        }
      }

      if (!hasErrors) {
        clearSelection();
        collapseAllMatings();
        onRefresh();
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Success',
            detail: 'Successfully updated all selected matings.',
            life: 3000,
          }),
        );
      }
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'Update OvaDrive failed.',
          life: 3000,
        }),
      );
    } finally {
      setIsUpdateRunning(false);
    }
  }, [
    selection,
    fertilizations,
    embryosByFert,
    batchForm,
    qcForm,
    updateODFreeze,
    setIsUpdateRunning,
    clearSelection,
    collapseAllMatings,
    onRefresh,
    dispatch,
  ]);

  /** Pre-update validation: check for incomplete temp embryos */
  const handleUpdateOvaDrive = useCallback(() => {
    const { fertilizationIds, tempEmbryoIds } = selection;

    if (
      !fertilizationIds.length &&
      !selection.liveEmbryoIds.length &&
      !tempEmbryoIds.length
    ) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'No Selection',
          detail: 'Select embryos or fertilizations before updating.',
          life: 3000,
        }),
      );
      return;
    }

    const incomplete: IncompleteEmbryo[] = [];

    for (const fertId of fertilizationIds) {
      const fert = fertilizations.find((f) => f.fertId === fertId);
      if (!fert) continue;

      const embryoData = embryosByFert?.get(fertId);
      if (!embryoData) continue;

      const fertTempIds = tempEmbryoIds.filter((id) =>
        embryoData.tempEmbryos?.some((e) => e.embryoFreezeEntryId === id),
      );

      for (const embryo of embryoData.tempEmbryos ?? []) {
        if (
          !embryo.embryoFreezeEntryId ||
          !fertTempIds.includes(embryo.embryoFreezeEntryId)
        )
          continue;

        const missingFields: string[] = [];
        if (!embryo.stage) missingFields.push('Stage');
        if (!embryo.grade) missingFields.push('Grade');
        if (!embryo.ownerId) missingFields.push('Owner');
        if (!batchForm.freezeDay && !embryo.d65 && !embryo.d7)
          missingFields.push('Freeze Day');
        if (!batchForm.graderId) missingFields.push('Grader');

        const isExport = fert.export !== 'No' && !!fert.export;
        if (isExport && !embryo.tt) missingFields.push('TT');

        if (missingFields.length > 0) {
          incomplete.push({
            plateNumber: fert.plateNumber ?? 0,
            embryoName: embryo.name ?? 'Unknown',
            missingFields,
          });
        }
      }
    }

    if (incomplete.length > 0) {
      incompleteEmbryosRef.current = incomplete;
      setShowIncompleteDialog(true);
    } else {
      setShowSummaryDialog(true);
    }
  }, [selection, fertilizations, embryosByFert, batchForm, dispatch]);

  /** Discard every currently-selected temp embryo in one action (parity with v1). */
  const handleDiscardSelectedTemp = useCallback(async () => {
    const ids = selection.tempEmbryoIds;
    if (!ids.length) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No Temp Embryos Selected',
          detail: 'Select temp embryos to discard.',
          life: 3000,
        }),
      );
      return;
    }
    try {
      await deactivateEmbryos(ids);
      selection.dispatch({ type: 'SET_TEMP_IDS', ids: [] });
      onRefresh();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Embryos Discarded',
          detail: `Discarded ${ids.length} temp embryo(s).`,
          life: 3000,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Discard Failed',
          detail: `${error}`,
          life: 5000,
        }),
      );
    }
  }, [selection, deactivateEmbryos, onRefresh, dispatch]);

  const handleExpandCollapse = useCallback(() => {
    if (!gridApi) return;
    const selectedNodes = gridApi.getSelectedNodes().filter((n) => n.master);
    if (selectedNodes.length === 0) {
      const newState = !isExpanded;
      gridApi.forEachNode((node) => {
        if (node.master) node.setExpanded(newState);
      });
      setIsExpanded(newState);
    } else {
      const allExpanded = selectedNodes.every((n) => n.expanded);
      selectedNodes.forEach((node) => node.setExpanded(!allExpanded));
    }
  }, [gridApi, isExpanded, setIsExpanded]);

  const handleBulkSelect = useCallback(
    (action: 'all-temp' | 'all-live' | 'clear-temp' | 'clear-live') => {
      if (!embryosByFert) return;

      if (action === 'clear-temp') {
        selection.dispatch({ type: 'SET_TEMP_IDS', ids: [] });
        return;
      }
      if (action === 'clear-live') {
        selection.dispatch({ type: 'SET_LIVE_IDS', ids: [] });
        return;
      }

      // Only select embryos from ferts currently visible after grid filtering (parity with v1).
      const visibleFertIds = new Set<string>();
      gridApi?.forEachNodeAfterFilter((node) => {
        if (node.data?.fertId) visibleFertIds.add(node.data.fertId);
      });
      const respectFilter = visibleFertIds.size > 0;

      const ids: string[] = [];
      const affectedFertIds = new Set<string>();
      embryosByFert.forEach((data, fertId) => {
        if (respectFilter && !visibleFertIds.has(fertId)) return;
        if (action === 'all-temp') {
          for (const e of data.tempEmbryos ?? []) {
            if (e.embryoFreezeEntryId) {
              ids.push(e.embryoFreezeEntryId);
              affectedFertIds.add(fertId);
            }
          }
        } else if (action === 'all-live') {
          for (const e of data.liveEmbryos ?? []) {
            if (e.id) {
              ids.push(e.id);
              affectedFertIds.add(fertId);
            }
          }
        }
      });

      const mergedFertIds = Array.from(
        new Set([...selection.fertilizationIds, ...affectedFertIds]),
      );

      if (action === 'all-temp') {
        selection.dispatch({ type: 'SET_TEMP_IDS', ids });
      } else if (action === 'all-live') {
        selection.dispatch({ type: 'SET_LIVE_IDS', ids });
      }
      selection.dispatch({ type: 'SET_FERT_IDS', ids: mergedFertIds });
    },
    [embryosByFert, selection, gridApi],
  );

  /* WI 37941 #6: Refresh left selections intact -- the button only refetched. Selection is
     persisted (and rehydrated) by design for v1 parity, so refetching alone never cleared it
     and users had no way out: hard refresh and leaving the app both restore from localStorage
     too. Clear first, then refetch -- same order the Update OvaDrive path already uses. */
  const handleRefreshClick = useCallback(() => {
    clearSelection();
    onRefresh();
  }, [clearSelection, onRefresh]);

  return (
    <div
      className={`flex gap-2 ${isMobile ? 'flex-wrap' : 'flex-row items-center'}`}
    >
      <Button
        variant="outline"
        size={isMobile ? 'sm' : 'default'}
        onClick={handleRefreshClick}
        disabled={isLoading}
      >
        <RefreshCw className={`size-3.5 ${isLoading ? 'animate-spin' : ''}`} />
        {!isMobile && 'Refresh'}
      </Button>

      <Button
        variant="outline"
        size={isMobile ? 'sm' : 'default'}
        onClick={handleExpandCollapse}
      >
        {isExpanded ? (
          <Minimize2 className="size-3.5" />
        ) : (
          <Maximize2 className="size-3.5" />
        )}
        {!isMobile && (isExpanded ? 'Collapse' : 'Expand')}
      </Button>

      {onDayFilterChange && (
        <div className="flex rounded-md border border-gray-200 dark:border-gray-700">
          {[
            { key: 'all' as DayFilter, label: 'All' },
            { key: 'd7' as DayFilter, label: 'D7' },
            { key: 'd6.5' as DayFilter, label: 'D6.5' },
          ].map(({ key, label }) => (
            <Button
              key={key}
              variant={activeDayFilter === key ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none first:rounded-l-md last:rounded-r-md"
              onClick={() => onDayFilterChange(key)}
            >
              {label}
            </Button>
          ))}
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button variant="outline" size={isMobile ? 'sm' : 'default'}>
              <CheckSquare className="size-3.5" />
              {!isMobile && 'Select'}
              <ChevronDown className="ml-1 size-3 opacity-60" />
            </Button>
          }
        />
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => handleBulkSelect('all-temp')}>
            Select All Temp Embryos
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleBulkSelect('all-live')}>
            Select All Live Embryos
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleBulkSelect('clear-temp')}>
            Unselect All Temp
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleBulkSelect('clear-live')}>
            Unselect All Live
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        variant="outline"
        size={isMobile ? 'sm' : 'default'}
        onClick={() => setShowDiscardDialog(true)}
        disabled={isDiscarding || selection.tempEmbryoIds.length === 0}
        title="Discard all selected temp embryos"
      >
        {isDiscarding ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Trash2 className="size-3.5 text-red-600" />
        )}
        {!isMobile && 'Discard Temp'}
        {selection.tempEmbryoIds.length > 0
          ? ` (${selection.tempEmbryoIds.length})`
          : ''}
      </Button>

      <Button
        variant="ghost"
        size={isMobile ? 'sm' : 'default'}
        onClick={() => setShowRulesDialog(true)}
        title="Embryo creation rules"
      >
        <Info className="size-3.5" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={isAnyReportGenerating}
          render={
            <Button
              variant="outline"
              size={isMobile ? 'sm' : 'default'}
              disabled={isAnyReportGenerating}
            >
              {isAnyReportGenerating ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Printer className="size-3.5" />
              )}
              {!isMobile && 'Print'}
              <ChevronDown className="ml-1 size-3 opacity-60" />
            </Button>
          }
        />
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => handleLabelPrint('GobletLabel')}>
            Goblet Labels
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleLabelPrint('StrawLabel')}>
            Straw Labels
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleLabelPrint('CaneTabLabel')}>
            Cane Tab Labels
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const date = params.get('date');
              const siteId = params.get('sId');
              if (!date || !siteId) {
                dispatch(
                  addToast({
                    severity: 'warn',
                    summary: 'Select date and site first',
                    life: 3000,
                  }),
                );
                return;
              }
              generateFreezeLabels({
                fertilizationDate: date,
                fertilizationSiteId: siteId,
              });
            }}
          >
            Freeze Labels (Excel)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const date = params.get('date');
              const siteId = params.get('sId');
              if (!date || !siteId) {
                dispatch(
                  addToast({
                    severity: 'warn',
                    summary: 'Select date and site first',
                    life: 3000,
                  }),
                );
                return;
              }
              generateTemplateReport({
                fertilizationDate: date,
                fertilizationSiteId: siteId,
              });
            }}
          >
            Freeze Template (Excel)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleRunSheet}>
            Run Sheet
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleVitrificationRunSheet}>
            Vitrification Run Sheet
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const date = params.get('date');
              const siteId = params.get('sId');
              if (!date || !siteId) {
                dispatch(
                  addToast({
                    severity: 'warn',
                    summary: 'Select date and site first',
                    life: 3000,
                  }),
                );
                return;
              }
              setShowEmbryoSummaryDialog(true);
            }}
          >
            Embryo Summary (PDF)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              if (selection.fertilizationIds.length === 0) {
                dispatch(
                  addToast({
                    severity: 'warn',
                    summary: 'Select fertilizations first',
                    life: 3000,
                  }),
                );
                return;
              }
              generateValidationReport({
                fertilizationIds: selection.fertilizationIds,
              });
            }}
          >
            Freeze Validation Report
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className={isMobile ? 'w-full' : 'ml-auto'}>
        <Button
          variant="default"
          size={isMobile ? 'sm' : 'default'}
          className={isMobile ? 'w-full' : ''}
          onClick={handleUpdateOvaDrive}
          disabled={isUpdating || isLoading || !hasSelection}
          title={
            !hasSelection
              ? 'Select one or more fertilizations or embryos to update'
              : undefined
          }
        >
          {isUpdating && <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />}
          Update OvaDrive
        </Button>
      </div>

      <IncompleteEmbryosDialog
        open={showIncompleteDialog}
        onOpenChange={setShowIncompleteDialog}
        incompleteEmbryos={incompleteEmbryosRef.current}
        onProceed={() => {
          setShowIncompleteDialog(false);
          setShowSummaryDialog(true);
        }}
        onCancel={() => setShowIncompleteDialog(false)}
      />

      <SelectionSummaryDialog
        open={showSummaryDialog}
        onOpenChange={setShowSummaryDialog}
        isLoading={isUpdating}
        fertilizations={fertilizations}
        embryosByFert={embryosByFert ?? new Map()}
        selectedFertIds={selection.fertilizationIds}
        selectedTempIds={selection.tempEmbryoIds}
        selectedLiveIds={selection.liveEmbryoIds}
        onProceed={() => {
          setShowSummaryDialog(false);
          executeUpdate();
        }}
        onCancel={() => setShowSummaryDialog(false)}
      />

      <EmbryoSummaryDialog
        open={showEmbryoSummaryDialog}
        onOpenChange={setShowEmbryoSummaryDialog}
        date={params.get('date')}
        siteId={params.get('sId')}
        siteName={siteName}
      />

      <RulesDialog open={showRulesDialog} onOpenChange={setShowRulesDialog} />

      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        title="Discard Temp Embryos"
        description={`Discard ${selection.tempEmbryoIds.length} selected temp embryo(s)? This cannot be undone.`}
        onConfirm={handleDiscardSelectedTemp}
        confirmLabel="Discard"
        confirmVariant="destructive"
        isLoading={isDiscarding}
      />
    </div>
  );
};
